import { Button } from "@hortis/ui/button";
import { Dot } from "@hortis/ui/dot";
import { Map2, Plus } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { Link } from "@tanstack/react-router";
import { AccessionFieldsFragment } from "generated/graphql";
import { useCreateMaterialStore } from "src/components/dialog/variants/create-material-dialog";
import {
  RecordSection,
  RecordSectionDataList,
  RecordSectionDataListItem,
  RecordSectionDataListItemProperties,
  RecordSectionDataListItemTitle,
  RecordSectionTitle,
} from "src/components/record-section";
import {
  PlantMaterialStatusToColor,
  PlantMaterialStatusToLabel,
} from "src/components/status-chip";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";

export const AccessionMaterials = ({
  accession,
  canEdit,
}: {
  accession: AccessionFieldsFragment;
  canEdit: boolean;
}) => {
  const { setCreateMaterialDialogOpen } = useCreateMaterialStore();
  const hasMaterials =
    accession.plantMaterial != null && accession.plantMaterial?.length > 0;
  return (
    <RecordSection>
      <RecordSectionTitle>
        Materials
        {hasMaterials && canEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                startIcon={Plus}
                size="sm"
                variant="tertiaryGray"
                onClick={() => {
                  setCreateMaterialDialogOpen(true, {
                    accession: {
                      id: accession.id,
                      scientificName: accession.taxon?.scientificName ?? "",
                      accessionNumber: accession.accessionNumber,
                    },
                  });
                }}
                testId="new-material-button"
              >
                New material
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Add new material</TooltipContent>
          </Tooltip>
        )}
      </RecordSectionTitle>
      <RecordSectionDataList>
        {!hasMaterials ? (
          canEdit ? (
            <RecordSectionDataListItem
              className="justify-center"
              data-cy="empty-state"
            >
              <Button
                startIcon={Plus}
                size="sm"
                variant="tertiaryGray"
                className="hover:bg-grey-100"
                onClick={() => {
                  setCreateMaterialDialogOpen(true, {
                    accession: {
                      id: accession.id,
                      scientificName: accession.taxon?.scientificName ?? "",
                      accessionNumber: accession.accessionNumber,
                    },
                  });
                }}
                testId="new-material-button"
              >
                Add first material
              </Button>
            </RecordSectionDataListItem>
          ) : (
            <RecordSectionDataListItem
              className="justify-center"
              data-cy="empty-state"
            >
              No materials
            </RecordSectionDataListItem>
          )
        ) : null}
        {accession.plantMaterial?.map((material) => (
          <Link
            to="/material/$id"
            params={{ id: material.id }}
            key={material.id}
            data-cy="material-row"
          >
            <RecordSectionDataListItem>
              <RecordSectionDataListItemTitle>
                <Dot color={PlantMaterialStatusToColor[material.status]} />
                {createMaterialNumber(
                  accession.accessionNumber,
                  material.qualifier,
                  "/",
                )}
              </RecordSectionDataListItemTitle>
              <RecordSectionDataListItemProperties>
                <div className="flex items-center gap-2">
                  <Map2 className="h-4 w-4 text-grey-500" />
                  <span className="truncate">
                    {material.collectionSiteLocation?.name}
                  </span>
                </div>
              </RecordSectionDataListItemProperties>
            </RecordSectionDataListItem>
          </Link>
        ))}
      </RecordSectionDataList>
    </RecordSection>
  );
};
